import {
  MEMBERSHIP_STATUS_NAMES,
  type MyWorkspace,
  TENANT_TYPE_NAMES,
  type TenantType,
} from "@closer/shared";
import { Badge, Button, Card, EmptyState, Spinner, useToast } from "@closer/ui";
import { useState } from "react";
import { useNavigate } from "react-router";

import { CreateTenantModal } from "../../components/CreateTenantModal";
import { UsageArchetypeChooser } from "../../components/UsageArchetypeChooser";
import { ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";

/**
 * Çalışma alanı seçim ekranı (CLOS-79 `[042]`).
 *
 * ⚠ Bu ekran starter'dan gelmişti ve hiçbir kart ona dokunmamıştı: her çalışma alanını
 * "Firma" diye listeliyor, kişisel alanı organizasyonlardan ayırmıyor ve askıya alınmış
 * üyeliği seçilebilir gösteriyordu — kullanıcı tıklıyor, `requireTenant` 403 veriyordu.
 * Kartın beş kabul kriteri de burada karşılanıyor.
 */

/**
 * Kanonik gruplama sırası: önce kişisel alan, sonra organizasyonlar, sonra müvekkil alanı.
 *
 * ⚠ `client_workspace` LİSTEDE OLMAK ZORUNDA. Sıra bir süzgeç olarak da çalışıyor: burada
 * olmayan tür ekrana hiç çizilmiyor. Müvekkil temsilcisinin TEK çalışma alanı sponsorlu
 * Müvekkil Alanı olabiliyor (CLOS-175 `[122]`, CLOS-178 `[132]`) ve tür listede yokken o
 * kullanıcı giriş yaptığında boş bir seçim ekranıyla kalıyordu — tıklayacağı satır yoktu.
 * Elle test bulguları: davet kabul edildi, temsil kuruldu, üyelik verildi ama alan
 * açılamadı.
 */
const TUR_SIRASI: readonly TenantType[] = [
  "individual",
  "enterprise_company",
  "enterprise_law_firm",
  "client_workspace",
];

function grupla(alanlar: MyWorkspace[]): Array<{ tur: TenantType; alanlar: MyWorkspace[] }> {
  return TUR_SIRASI.map((tur) => ({
    tur,
    alanlar: alanlar.filter((a) => a.type === tur),
  })).filter((grup) => grup.alanlar.length > 0);
}

/**
 * Seçilemeyen alanın durum rozeti.
 *
 * Durum adı `MEMBERSHIP_STATUS_NAMES`'ten geliyor; burada ikinci bir eşleme tutulsaydı
 * sunucunun bildiği durum kümesiyle ekranınki ayrışır ve kullanıcı ham `suspended`
 * görürdü.
 */
function DurumRozeti({ alan }: { alan: MyWorkspace }) {
  if (alan.selectable) return null;
  return (
    <Badge tone={alan.status === "suspended" ? "warning" : "danger"}>
      {MEMBERSHIP_STATUS_NAMES[alan.status]}
    </Badge>
  );
}

export function TenantsPage() {
  const {
    memberships,
    isLoading,
    membershipsLoading,
    membershipsError,
    switchWorkspace,
    user,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);

  /**
   * Geçiş SUNUCUDAN doğrulanarak yapılıyor (CLOS-80 `[043]`).
   *
   * Liste bayat olabilir: satır seçilebilir görünürken üyelik bu arada askıya alınmış
   * olabilir. `switchWorkspace` önce hedefin bağlamını çekiyor; reddedilirse bağlam
   * DEĞİŞMİYOR, kullanıcı bulunduğu yerde kalıyor ve liste tazeleniyor.
   */
  async function selectTenant(tenantId: string) {
    try {
      await switchWorkspace(tenantId);
      navigate("/app");
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : "Çalışma alanına geçilemedi");
    }
  }

  async function handleLogout() {
    await logout();
    toast.info("Oturum kapatıldı");
    navigate("/login");
  }

  const gruplar = grupla(memberships);

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-lg flex-col justify-center gap-4 p-4">
      <Card
        title="Çalışma Alanı Seç"
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            Yeni Organizasyon
          </Button>
        }
      >
        {/* Oturum VEYA üyelik listesi yüklenirken bekle: erken bakılsaydı alanı OLAN
            kullanıcıya bir an "alanınız yok" boş durumu çakardı (denetim bulgusu). */}
        {isLoading || membershipsLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : membershipsError ? (
          // Ağ/sunucu hatası "alanınız yok" olarak MASKELENMEZ: farklı sorun, farklı çare.
          <EmptyState
            title="Çalışma alanlarınız yüklenemedi"
            description="Bağlantı sorunu olabilir."
            action={<Button onClick={() => window.location.reload()}>Yeniden dene</Button>}
          />
        ) : memberships.length === 0 ? (
          /* Kişisel çalışma alanı hesap açılışında doğuyor ve liste ucu onu ayrıca garanti
             ediyor (CLOS-70 `[022]`), yani bu durum beklenmez. Yine de duruyor: garanti
             FIRLATMAYAN bir güvenlik ağı ve boş bir liste, açıklamasız ekrandan iyidir. */
          <EmptyState
            title="Çalışma alanı bulunamadı"
            description="Kişisel çalışma alanınız hazırlanamamış olabilir. Sayfayı yenileyin."
            action={<Button onClick={() => window.location.reload()}>Yeniden dene</Button>}
          />
        ) : (
          <div className="flex flex-col gap-5">
            {gruplar.map((grup) => (
              <section key={grup.tur} className="flex flex-col gap-2">
                <h3 className="text-xs font-semibold tracking-wide text-fg-soft uppercase">
                  {TENANT_TYPE_NAMES[grup.tur]}
                </h3>
                <ul className="flex flex-col gap-2">
                  {grup.alanlar.map((alan) => (
                    <li key={alan.tenantId}>
                      <Button
                        variant="secondary"
                        /* Seçilemeyen alan TIKLANAMAZ (kart: "yalnız aktif veya erişilebilir
                           alanlar seçilebilir"). Karar sunucudan gelen `selectable`
                           alanında; istemci durumu yeniden yorumlamıyor. */
                        disabled={!alan.selectable}
                        onClick={() => void selectTenant(alan.tenantId)}
                        className="!h-auto w-full !justify-between gap-3 !text-left"
                      >
                        <span className="min-w-0 flex-1">
                          <span
                            className={
                              alan.selectable
                                ? "block truncate text-sm font-medium text-fg"
                                : "block truncate text-sm font-medium text-fg-soft"
                            }
                          >
                            {alan.name}
                          </span>
                          {/*
                            ⚠ HUKUK BÜROSU SUNUCUDAN (CLOS-1123): alan adı düzenlenebilir;
                            "hangi büronun alanı" bilgisi `sponsorName` projeksiyonundan gelir,
                            addan AYRIŞTIRILMAZ. Aynı müvekkilin iki büroyla iki alanı burada
                            ayrılır; ad değişse de etiket kalır.
                          */}
                          {alan.type === "client_workspace" && alan.sponsorName ? (
                            <span className="block text-xs text-fg-soft">
                              {`Müvekkil Alanı · ${alan.sponsorName}`}
                            </span>
                          ) : null}
                          {alan.roleName ? (
                            <span className="block text-xs text-fg-soft">{alan.roleName}</span>
                          ) : null}
                        </span>
                        <DurumRozeti alan={alan} />
                      </Button>
                      {/*
                        ⚠ KULLANIM BİÇİMİ SEÇİM DÜĞMESİNİN DIŞINDA (CLOS-808): alan seçme
                        düğmesinin içine konsaydı iç içe düğme doğar ve radyoya tıklamak
                        alanı da seçerdi. Yeri burası çünkü seçim ÇALIŞMA ALANINA ait —
                        hesap ekranına konsaydı kartın yasakladığı "global meslek alanı"
                        görünümü doğardı.
                      */}
                      {alan.type === "individual" ? (
                        <div className="mt-2">
                          <UsageArchetypeChooser alan={alan} />
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </Card>
      <p className="text-center text-sm text-fg-soft">
        {user?.email} —{" "}
        <Button
          variant="ghost"
          onClick={() => void handleLogout()}
          className="!inline-flex !h-auto !px-1 !py-0 align-baseline font-medium !text-accent hover:underline"
        >
          Çıkış
        </Button>
      </p>
      <CreateTenantModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
